import React, { useRef, useEffect, useCallback } from "react";
import styles from './SemanticSearch.module.css';
import answerStyles from './AnswerComponent.module.css';
import { SearchInPdfInput } from "./search_in_pdf_input";
import { AnswerComponent } from './chatgpt_answer.js';
import { SourcePreview } from "./components/SourcePreview/SourcePreview";
import { TicketPreviewCard } from "./components/TicketPreviewCard";
import { EmailPreviewCard } from "./components/EmailPreviewCard";
import { PDFPagePreview } from "./pdf_page_preview";
import { SourceItem, SearchState } from "./semantic_search.types";
import { useSemanticSearchStore } from "./store/SemanticSearchContext";
import { General } from "../../siteManagement/general";
import { SearchMode, SearchTarget, SortOrder } from "./store/semantic_search.store.types";

const SearchResultItem: React.FC<{ item: SourceItem; index: number }> = ({ item, index }) => {
    const selectedListItem = useSemanticSearchStore(state => state.selectedListItem);
    const checkedSources = useSemanticSearchStore(state => state.checkedSources);
    const searchState = useSemanticSearchStore(state => state.searchState);
    const setSelectedListItem = useSemanticSearchStore(state => state.setSelectedListItem);
    const favorites = useSemanticSearchStore(state => state.favorites);
    const toggleFavorite = useSemanticSearchStore(state => state.toggleFavorite);

    const isSelected = selectedListItem?.SourceId === item.SourceId;
    const style: React.CSSProperties = {
        border: "1px solid",
        borderColor: isSelected ? "#007bff" : "#ddd",
        boxShadow: isSelected ? "0 0 0 1px #007bff" : "none",
        backgroundColor: isSelected ? "#e7f3ff" : "#fff",
        borderRadius: "5px",
        cursor: "pointer",
        margin: "8px"
    };

    const isUsed = checkedSources?.UsedSources?.some((i) => parseInt(i.SourceId, 10) === index);
    const icon = checkedSources && isUsed
        ? <i className="fa-solid fa-2x fa-check" style={{ color: 'green', marginRight: '8px' }}></i>
        : searchState === SearchState.ValidatingSources ? <div className={answerStyles.loader} style={{ marginRight: '8px', flexShrink: 0 }}></div>
            : null;

    const isFavorite = favorites.some(f => f.SourceId === item.SourceId);

    if (item.Type === "ticket") {
        return (
            <li style={style} onClick={() => setSelectedListItem(item)}>
                <TicketPreviewCard
                    ticket={item}
                    icon={icon}
                    isFavorite={isFavorite}
                    onToggleFavorite={() => toggleFavorite(item)}
                />
            </li>
        );
    }

    if (item.Type === "email") {
        return (
            <li style={style} onClick={() => setSelectedListItem(item)}>
                <EmailPreviewCard
                    email={item}
                    icon={icon}
                    isFavorite={isFavorite}
                    onToggleFavorite={() => toggleFavorite(item)}
                />
            </li>
        );
    }


    let preview = null;
    if (item.Type === "pdf") {
        preview = <PDFPagePreview pdfGuid={item.IsGuid} pageNumber={item.Page} />;
    } else if (item.Type === "documentation") {
        preview = <div style={{ height: "350px", display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>Dokumentation</div>;
    }

    return (
        <li style={style} onClick={() => setSelectedListItem(item)}>
            <div style={{ padding: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', fontWeight: "bold", fontSize: "14px", marginBottom: "10px", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                    {icon}{item.Title}
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid #eee' }} />
                {preview}
            </div>
        </li>
    );
};


export const SemanticSearchView: React.FC = () => {
    const listRef = useRef<HTMLUListElement>(null);
    const searchInputRef = useRef<SearchInPdfInput>(null);

    const searchResult = useSemanticSearchStore(state => state.searchResult);
    const textToHighlight = useSemanticSearchStore(state => state.textToHighlight);
    const checkedSources = useSemanticSearchStore(state => state.checkedSources);
    const searchState = useSemanticSearchStore(state => state.searchState);
    const hasMoreResults = useSemanticSearchStore(state => state.hasMoreResults);
    const isChatGptEnabled = useSemanticSearchStore(state => state.isChatGptEnabled);
    const searchMode = useSemanticSearchStore(state => state.searchMode);
    const setSearchMode = useSemanticSearchStore(state => state.setSearchMode);
    const searchTarget = useSemanticSearchStore(state => state.searchTarget);
    const setSearchTarget = useSemanticSearchStore(state => state.setSearchTarget);
    const sortOrder = useSemanticSearchStore(state => state.sortOrder);
    const setSortOrder = useSemanticSearchStore(state => state.setSortOrder);
    const startSearch = useSemanticSearchStore(state => state.startSearch);
    const loadMoreResults = useSemanticSearchStore(state => state.loadMoreResults);

    useEffect(() => {
        General.Sheet.Manager.getTopMostSheet().setTitle("Suchen");
        General.Drawer.Left.disable();
    }, []);
    
    const handleScroll = useCallback(() => {
        const listElement = listRef.current;
        if (listElement) {
            const { scrollTop, scrollHeight, clientHeight } = listElement;
            if (scrollTop + clientHeight >= scrollHeight - 100) { // 100px threshold
                loadMoreResults();
            }
        }
    }, [loadMoreResults]);

    useEffect(() => {
        const listElement = listRef.current;
        if (listElement) {
            listElement.addEventListener('scroll', handleScroll);
            return () => listElement.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    const isLoadingMore = searchState === SearchState.LoadingSources && searchResult.length > 0;
    
    const renderSearchModeOption = (mode: SearchMode, label: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
                type="radio"
                id={`search-mode-${mode}`}
                name="search-mode"
                value={mode}
                checked={searchMode === mode}
                onChange={() => setSearchMode(mode)}
                style={{ margin: 0, cursor: 'pointer' }}
            />
            <label htmlFor={`search-mode-${mode}`} style={{ cursor: 'pointer', userSelect: 'none' }}>
                {label}
            </label>
        </div>
    );

    const renderSearchTargetOption = (target: SearchTarget, label: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
                type="radio"
                id={`search-target-${target}`}
                name="search-target"
                value={target}
                checked={searchTarget === target}
                onChange={() => setSearchTarget(target)}
                style={{ margin: 0, cursor: 'pointer' }}
            />
            <label htmlFor={`search-target-${target}`} style={{ cursor: 'pointer', userSelect: 'none' }}>
                {label}
            </label>
        </div>
    );

    const renderSortOrderOption = (order: SortOrder, label: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
                type="radio"
                id={`sort-order-${order}`}
                name="sort-order"
                value={order}
                checked={sortOrder === order}
                onChange={() => setSortOrder(order)}
                style={{ margin: 0, cursor: 'pointer' }}
            />
            <label htmlFor={`sort-order-${order}`} style={{ cursor: 'pointer', userSelect: 'none' }}>
                {label}
            </label>
        </div>
    );

    return (
        <div className={styles.container}>
            <main className={styles.mainArea}>
                <div className={styles.content}>
                    <SourcePreview
                        textToHighlight={textToHighlight ?? undefined}
                    />
                </div>
                <aside className={styles.sidebar}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <SearchInPdfInput ref={searchInputRef} initValue="" onChange={startSearch} />
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-start', fontSize: '0.85rem' }}>
                            <strong>Suchen in:</strong>
                            {renderSearchTargetOption('all', 'Alles')}
                            {renderSearchTargetOption('tickets', 'Tickets')}
                            {renderSearchTargetOption('emails', 'E-Mails')}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-start', fontSize: '0.85rem' }}>
                            <strong>Suchmodus:</strong>
                            {renderSearchModeOption('fuzzy', 'Unscharfe Suche')}
                            {renderSearchModeOption('wildcard', 'Wildcard-Suche')}
                            {renderSearchModeOption('exact', 'Exakte Suche')}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-start', fontSize: '0.85rem' }}>
                            <strong>Sortieren nach:</strong>
                            {renderSortOrderOption('relevance', 'Relevanz')}
                            {renderSortOrderOption('date', 'Datum')}
                        </div>
                    </div>
                    <AnswerComponent
                        chatGptEnabled={isChatGptEnabled}
                        answer={checkedSources?.Answer}
                        usedSources={checkedSources?.UsedSources}
                        searchstate={searchState}
                    />
                    <section className={styles.listSection}>
                        <header className={styles.sectionHeader}>
                            <h5 className={styles.sectionTitle}>Quellen</h5>
                        </header>
                        <div className={styles.listContainer} style={{flexDirection: 'column'}}>
                            <ul className={styles.list} ref={listRef}>
                                {searchResult.map((item, index) => (
                                    <SearchResultItem key={item.SourceId} item={item} index={index} />
                                ))}
                                {hasMoreResults && (
                                    <li
                                        className={`${styles.loadMoreItem} ${isLoadingMore ? styles.loading : ''}`}
                                        onClick={isLoadingMore ? undefined : loadMoreResults}
                                    >
                                        {isLoadingMore ? <div className={styles.loader}></div> : 'Mehr laden'}
                                    </li>
                                )}
                            </ul>
                        </div>
                    </section>
                </aside>
            </main>
        </div>
    );
};